import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Shield, Check, ArrowRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import VideoBackground from '../VideoBackground';
import { useStripe } from '@/hooks/use-stripe';
import { initiatePayment, generateReference } from '@/services/paystackService';
import { useAIVideoStore } from '@/hooks/use-ai-video-store';
interface Step10PaymentProps {
  onNext: () => void;
  onBack: () => void;
  answers: any;
}
const Step10Payment: React.FC<Step10PaymentProps> = ({
  onNext,
  onBack,
  answers
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('nigeria');
  const [customerName, setCustomerName] = useState('');
  const {
    toast
  } = useToast();
  const {
    createPayment
  } = useStripe();
  const {
    answers: storeAnswers
  } = useAIVideoStore();
  const handlePayment = async () => {
    if (!customerName.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter your full name to proceed.",
        variant: "destructive"
      });
      return;
    }
    if (!storeAnswers.email) {
      toast({
        title: "Email Required",
        description: "Please provide your email address.",
        variant: "destructive"
      });
      return;
    }
    setIsLoading(true);
    try {
      if (selectedCountry === 'nigeria') {
        // Use Paystack for Nigerian customers
        const reference = generateReference();
        const paymentData = {
          amount: 999900,
          // ₦9,999 in kobo (Nigerian cents)
          reference,
          metadata: {
            custom_fields: [{
              name: 'product',
              value: 'AI Video Creation Course'
            }, {
              name: 'video_goal',
              value: storeAnswers.videoGoal || ''
            }, {
              name: 'experience',
              value: storeAnswers.experience || ''
            }, {
              name: 'challenge',
              value: storeAnswers.challenge || ''
            }, {
              name: 'impact',
              value: storeAnswers.impact || ''
            }, {
              name: 'ai_familiarity',
              value: storeAnswers.aiFamiliarity || ''
            }]
          }
        };
        const customerData = {
          name: customerName,
          email: storeAnswers.email,
          phone: storeAnswers.phone || '+234'
        };
        const authorizationUrl = await initiatePayment(customerData, paymentData);

        // Redirect to Paystack
        window.location.href = authorizationUrl;
      } else {
        // Use Stripe for international customers
        const result = await createPayment({
          customerName,
          customerEmail: storeAnswers.email,
          customerPhone: storeAnswers.phone || '+1',
          serviceType: 'AI Video Creation Course',
          serviceTier: 'Premium',
          price: 9.99,
          // $9.99 in dollars (will be converted to cents in Stripe function)
          projectDescription: `AI Video Course - Customer answers: ${JSON.stringify(storeAnswers)}`
        });
        if (result.success) {
          // Stripe will handle redirection
          return;
        }
      }
    } catch (error: any) {
      toast({
        title: "Payment Failed",
        description: error.message || "Please try again or contact support.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const features = ['Complete AI Video Creation Masterclass', 'Personalized Learning Blueprint', 'Advanced AI Tools & Techniques', 'Script to Screen Automation', 'Character & Voice Generation', 'Professional Templates Library', 'Lifetime Access to Updates', '30-Day Money-Back Guarantee'];
  const fallbackBackground = <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
      <motion.div className="absolute inset-0 opacity-20" animate={{
      background: ["radial-gradient(circle at 20% 50%, #ffffff 1px, transparent 1px)", "radial-gradient(circle at 80% 50%, #ffffff 1px, transparent 1px)", "radial-gradient(circle at 40% 50%, #ffffff 1px, transparent 1px)"]
    }} transition={{
      duration: 10,
      repeat: Infinity
    }} />
    </div>;
  return <div className="min-h-screen relative overflow-hidden flex flex-col">
      <VideoBackground videoSrc="https://res.cloudinary.com/drinkamrs/video/upload/v1752753031/Characters_wole_ai_02_a_202507170450_g9mto0.mp4" fallbackComponent={fallbackBackground} />
      <div className="relative z-10 flex items-center justify-between p-4 bg-white/10 backdrop-blur-sm border-b border-white/20">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowRight className="w-4 h-4 rotate-180" />
          <span>Back</span>
        </button>
        <div className="flex items-center gap-2">
          <img src="/lovable-uploads/120101b5-df74-479f-bfcb-7643f8fd8af8.png" alt="LovaBuild" className="h-8 w-auto" />
          <span className="text-lg font-semibold text-gray-900">LovaBuild</span>
        </div>
        <div className="w-12"></div>
      </div>

      <div className="relative z-10 flex-1 px-6 py-16 flex items-center justify-center">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="max-w-md mx-auto w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <motion.div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-hollywood-gold-dark to-hollywood-gold-light rounded-full flex items-center justify-center" initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            duration: 0.5
          }}>
              <CreditCard className="w-8 h-8 text-white" />
            </motion.div>
            <h2 className="text-3xl font-bold mb-2 text-gray-50">
              Secure Your AI Video Creation Journey!
            </h2>
            <p className="text-yellow-600">
              Join thousands mastering AI video creation
            </p>
          </div>

          {/* Pricing Card */}
          <motion.div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 mb-6" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }}>
            {/* Customer Name Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name:
              </label>
              <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Enter your full name" className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-black" />
            </div>

            {/* Country Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select your location:
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setSelectedCountry('nigeria')} className={`p-3 rounded-xl border-2 transition-all ${selectedCountry === 'nigeria' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div className="text-center">
                    <div className="text-2xl mb-1">🇳🇬</div>
                    <div className="font-semibold text-gray-900">Nigeria/Africa</div>
                    <div className="text-lg font-bold text-purple-600">₦9,999</div>
                  </div>
                </button>
                <button onClick={() => setSelectedCountry('international')} className={`p-3 rounded-xl border-2 transition-all ${selectedCountry === 'international' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div className="text-center">
                    <div className="text-2xl mb-1">🌍</div>
                    <div className="font-semibold text-gray-900">International</div>
                    <div className="text-lg font-bold text-purple-600">$9.99</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Features */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-4">What's included:</h3>
              <div className="space-y-2">
                {features.map((feature, index) => <div key={index} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>)}
              </div>
            </div>

            {/* Security */}
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
              <Shield className="w-4 h-4" />
              <span>
                {selectedCountry === 'nigeria' ? 'Secured by Paystack' : 'Secured by Stripe'} - 256-bit SSL encrypted
              </span>
            </div>

            {/* Payment Button */}
            <button onClick={handlePayment} disabled={isLoading || !customerName.trim()} className="w-full bg-gradient-to-r from-hollywood-gold-dark to-hollywood-gold-light text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
              {isLoading ? <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </div> : `Pay with ${selectedCountry === 'nigeria' ? 'Paystack ₦9,999' : 'Stripe $9.99'}`}
            </button>

            <p className="text-xs text-gray-500 text-center mt-4">
              30-day money-back guarantee. Cancel anytime.
            </p>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div className="text-center" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }}>
            <p className="text-sm text-gray-500 mb-2">Trusted by 10,000+ creators worldwide</p>
            <div className="flex justify-center gap-4 text-xs text-gray-400">
              <span>🔒 Secure</span>
              <span>⚡ Instant Access</span>
              <span>💎 Premium</span>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Footer */}
      <div className="p-4 text-center text-xs text-gray-500">
        <div className="flex flex-wrap justify-center gap-4 mb-2">
          <a href="#" className="hover:text-gray-700 transition-colors">
            Privacy Policy
          </a>
          <a href="#" className="hover:text-gray-700 transition-colors">
            Terms & Conditions
          </a>
        </div>
        <p>© 2024 iSelect Digital. All rights reserved.</p>
      </div>
    </div>;
};
export default Step10Payment;